import * as Cesium from 'cesium';
import type { ObjectType, TleRecord } from '../api/types';
import type { Clock } from '../time/Clock';
import { onMotionPreferenceChange, prefersReducedMotion } from '../util/motion';
import type { LoadedReply, PositionsReply } from '../workers/propagator';
import {
  DEFAULT_GNSS_FLAGS,
  buildMembershipIndex,
  pickGnssStyle,
  type GnssConstellation,
  type GnssMembershipPayload,
  type GnssOverlayFlags,
} from './gnssColoring';

const PROPAGATE_INTERVAL_MS = 250; // ~4 Hz; LEO sats move <800m per tick
/** A clock-time delta this large bypasses the throttle (e.g. a slider scrub). */
const BIG_JUMP_MS = 5000;

const COLORS: Record<ObjectType, Cesium.Color> = {
  PAYLOAD:     Cesium.Color.fromCssColorString('#00d9ff'),  // cyan
  ROCKET_BODY: Cesium.Color.fromCssColorString('#ffb700'),  // amber
  DEBRIS:      Cesium.Color.fromCssColorString('#ff3860'),  // red
  TBA:         Cesium.Color.fromCssColorString('#888888'),  // gray
  UNKNOWN:     Cesium.Color.fromCssColorString('#00d9ff'),  // cyan (most are payloads)
};

const PIXEL_SIZES: Record<ObjectType, number> = {
  PAYLOAD: 3,
  ROCKET_BODY: 2.5,
  DEBRIS: 2,
  TBA: 2,
  UNKNOWN: 3,
};

const HIGHLIGHT_COLOR = Cesium.Color.WHITE;
const HIGHLIGHT_PIXEL_SIZE = 9;

/** Selection-pulse animation parameters. The dot oscillates between
 *  (BASE − AMPLITUDE) and (BASE + AMPLITUDE) pixels with PULSE_PERIOD,
 *  while its hue rotates through the full spectrum with HUE_PERIOD. */
const HIGHLIGHT_PULSE_AMPLITUDE = 2;
const HIGHLIGHT_PULSE_PERIOD_MS = 1500;
const HIGHLIGHT_HUE_PERIOD_MS = 3500;
const HIGHLIGHT_HUE_LIGHTNESS = 0.6;
const HIGHLIGHT_HUE_SATURATION = 1.0;

/** Phase 8 chunk 1 — static-dot values when the user has requested
 *  reduced motion. Per docs/phase8.md acceptance criterion: size 11 px,
 *  accent-cyan, no hue rotation, no size oscillation. */
const HIGHLIGHT_REDUCED_PIXEL_SIZE = 11;
const HIGHLIGHT_REDUCED_COLOR = Cesium.Color.fromCssColorString('#00d9ff');

/**
 * Owns a Cesium PointPrimitiveCollection and a propagator Web Worker.
 * Pulls bulk TLEs from the API, hands them to the worker, then
 * polls the worker at PROPAGATE_INTERVAL_MS and updates each
 * primitive's position with the returned ECEF coordinates.
 */
export class PointPrimitiveLayer {
  private collection: Cesium.PointPrimitiveCollection;
  private worker: Worker;
  /** norad_id → primitive index */
  private indexByNorad = new Map<number, number>();
  /** norad_id → object_type (used to restore color when un-highlighting) */
  private typeByNorad = new Map<number, string>();
  /** norad_id → most recent ECEF position (meters) */
  private positionByNorad = new Map<number, Cesium.Cartesian3>();
  /** norad_id → raw TLE strings (used by chunk-2 OrbitRibbonLayer to rebuild satrecs) */
  private tleByNorad = new Map<number, { line1: string; line2: string }>();
  /** norad_id → display name (used by chunk-3 marquee registry name-prefix matcher) */
  private nameByNorad = new Map<number, string>();
  private highlighted: number | null = null;
  private highlightStartMs = 0;
  private highlightRaf: number | null = null;
  /** Phase 8 chunk 1 — unsubscribe handle for the motion-preference
   *  change listener. Set in the constructor; called in destroy(). */
  private unsubscribeMotion: (() => void) | null = null;
  private unsubscribeTick: (() => void) | null = null;
  private lastPropagatedClockMs = 0;
  private lastPropagateRealMs = 0;
  private destroyed = false;

  /** Phase 9 chunk 2 — GNSS membership + overlay state. */
  private gnssMembership: Map<number, GnssConstellation> = new Map();
  private gnssFlags: GnssOverlayFlags = { ...DEFAULT_GNSS_FLAGS };
  private gnssRepaintTimer: ReturnType<typeof setTimeout> | null = null;

  /** Number of satellites currently rendered. */
  public count = 0;
  public onStatusChange: ((status: string) => void) | null = null;

  constructor(
    private readonly scene: Cesium.Scene,
    private readonly clock: Clock,
  ) {
    this.collection = scene.primitives.add(new Cesium.PointPrimitiveCollection());
    this.worker = new Worker(
      new URL('../workers/propagator.ts', import.meta.url),
      { type: 'module' }
    );
    this.worker.onmessage = (e: MessageEvent<LoadedReply | PositionsReply>) =>
      this.onWorkerMessage(e.data);
    // Phase 8 chunk 1 — subscribe to OS-level reduced-motion preference
    // changes so an active selection switches gracefully between the
    // animated pulse and the static dot without needing a re-click.
    this.unsubscribeMotion = onMotionPreferenceChange(this.handleMotionPreferenceChange);
  }

  /**
   * Replace the rendered set with `records`. Spawns one PointPrimitive
   * per record at the origin (worker will fix positions on the next tick).
   */
  load(records: TleRecord[]): void {
    if (this.destroyed) return;
    this.cancelHighlightAnimation();
    this.collection.removeAll();
    this.indexByNorad.clear();
    this.typeByNorad.clear();
    this.positionByNorad.clear();
    this.tleByNorad.clear();
    this.nameByNorad.clear();
    this.highlighted = null;

    for (let i = 0; i < records.length; i++) {
      const r = records[i];
      const type = r.object_type ?? 'UNKNOWN';
      this.collection.add({
        position: Cesium.Cartesian3.ZERO, // worker will overwrite on first propagate
        color: COLORS[type] ?? COLORS.UNKNOWN,
        pixelSize: PIXEL_SIZES[type] ?? PIXEL_SIZES.UNKNOWN,
        outlineColor: Cesium.Color.BLACK.withAlpha(0.4),
        outlineWidth: 0,
        id: r.norad_id, // available via scene.pick().id
      });
      this.indexByNorad.set(r.norad_id, i);
      this.typeByNorad.set(r.norad_id, type);
      this.tleByNorad.set(r.norad_id, { line1: r.line1, line2: r.line2 });
      this.nameByNorad.set(r.norad_id, r.name);
    }
    this.count = records.length;

    this.worker.postMessage({ type: 'load', tles: records });
  }

  /** Latest known ECEF position (meters) for the given NORAD, if any. */
  getPosition(norad: number): Cesium.Cartesian3 | null {
    return this.positionByNorad.get(norad) ?? null;
  }

  /** Iterable view onto every (NORAD, position) pair the worker has reported.
   * Used by chunk-6D's "tracking N satellites from this station" tooltip,
   * which iterates ~15k positions and runs an elevation check against each. */
  iteratePositions(): IterableIterator<[number, Cesium.Cartesian3]> {
    return this.positionByNorad.entries();
  }

  /** Raw TLE lines for the given NORAD, if loaded.  Used to build a fresh
   * satrec on the main thread for the chunk-2 orbit ribbon (the worker
   * has its own satrecs but doesn't expose them across threads).
   */
  getTle(norad: number): { line1: string; line2: string } | null {
    return this.tleByNorad.get(norad) ?? null;
  }

  /** Display name for the given NORAD, if loaded.  Used by chunk-3 marquee registry. */
  getName(norad: number): string | null {
    return this.nameByNorad.get(norad) ?? null;
  }

  /**
   * Visually emphasize a single satellite — pulses in size and rotates
   * its hue through the full spectrum so the user can pick it out at a
   * glance. Pass null to clear; the previous selection's default style
   * is restored. Driven by requestAnimationFrame; cancels cleanly on
   * deselect / load / destroy.
   *
   * Phase 8 chunk 1 — when the user has requested reduced motion at
   * the OS level, the pulse is replaced with a static high-contrast
   * dot (size 11 px, accent-cyan; no hue rotation, no size oscillation).
   * The runtime listener on {@link onMotionPreferenceChange} handles
   * the preference flipping mid-selection without needing a new click.
   */
  setHighlight(norad: number | null): void {
    if (this.highlighted === norad) return;
    this.cancelHighlightAnimation();
    if (this.highlighted !== null) {
      this.applyDefaultStyle(this.highlighted);
    }
    this.highlighted = norad;
    if (norad !== null && this.indexByNorad.has(norad)) {
      if (prefersReducedMotion()) {
        this.applyStaticHighlight();
      } else {
        this.highlightStartMs = performance.now();
        this.highlightRaf = requestAnimationFrame(this.animateHighlight);
      }
    }
  }

  /** Phase 8 chunk 1 — set the highlighted dot to its static
   *  reduced-motion appearance (no RAF). Used both for the initial
   *  highlight when reduced motion is already on, and when the
   *  user flips the preference mid-selection. */
  private applyStaticHighlight(): void {
    if (this.highlighted === null) return;
    const idx = this.indexByNorad.get(this.highlighted);
    if (idx === undefined) return;
    const p = this.collection.get(idx);
    p.color = HIGHLIGHT_REDUCED_COLOR;
    p.pixelSize = HIGHLIGHT_REDUCED_PIXEL_SIZE;
  }

  /** Phase 8 chunk 1 — runtime preference handler: gracefully switch
   *  between pulse + static states when the user flips the OS-level
   *  reduced-motion setting without re-selecting a satellite. */
  private handleMotionPreferenceChange = (reduced: boolean): void => {
    if (this.highlighted === null) return;
    this.cancelHighlightAnimation();
    if (reduced) {
      this.applyStaticHighlight();
    } else {
      this.highlightStartMs = performance.now();
      this.highlightRaf = requestAnimationFrame(this.animateHighlight);
    }
  };

  private cancelHighlightAnimation(): void {
    if (this.highlightRaf !== null) {
      cancelAnimationFrame(this.highlightRaf);
      this.highlightRaf = null;
    }
  }

  private animateHighlight = (): void => {
    if (this.destroyed || this.highlighted === null) {
      this.highlightRaf = null;
      return;
    }
    const idx = this.indexByNorad.get(this.highlighted);
    if (idx === undefined) {
      this.highlightRaf = null;
      return;
    }
    const elapsed = performance.now() - this.highlightStartMs;
    // Size pulse: cosine wave so the dot starts at its base size and
    // grows on the first beat rather than starting mid-cycle.
    const pulsePhase = (elapsed / HIGHLIGHT_PULSE_PERIOD_MS) * 2 * Math.PI;
    const pixelSize = HIGHLIGHT_PIXEL_SIZE + HIGHLIGHT_PULSE_AMPLITUDE * Math.sin(pulsePhase);
    // Hue rotation: 0..1 over HIGHLIGHT_HUE_PERIOD_MS, full spectrum.
    const hue = (elapsed / HIGHLIGHT_HUE_PERIOD_MS) % 1;
    const p = this.collection.get(idx);
    p.color = Cesium.Color.fromHsl(hue, HIGHLIGHT_HUE_SATURATION, HIGHLIGHT_HUE_LIGHTNESS);
    p.pixelSize = pixelSize;
    this.highlightRaf = requestAnimationFrame(this.animateHighlight);
  };

  private applyDefaultStyle(norad: number): void {
    const idx = this.indexByNorad.get(norad);
    if (idx === undefined) return;
    const p = this.collection.get(idx);
    // Phase 9 chunk 2 — GNSS constellation overlay wins over the
    // type-default color when the satellite is a GNSS member AND
    // its constellation's overlay is on. Falls through to type
    // styling otherwise so non-GNSS dots (and GNSS members with
    // their overlay off) stay cyan/amber/red as before.
    const gnss = pickGnssStyle(norad, this.gnssMembership, this.gnssFlags);
    if (gnss !== null) {
      p.color = Cesium.Color.fromCssColorString(gnss.color);
      p.pixelSize = gnss.pixelSize;
      return;
    }
    const type = (this.typeByNorad.get(norad) ?? 'UNKNOWN') as keyof typeof COLORS;
    p.color = COLORS[type] ?? COLORS.UNKNOWN;
    p.pixelSize = PIXEL_SIZES[type] ?? PIXEL_SIZES.UNKNOWN;
  }

  /**
   * Phase 9 chunk 2 — wire up the GNSS membership map fetched from
   * `/api/v1/gnss/membership` at boot. Idempotent re-calls swap the
   * map and repaint affected dots; the worker isn't touched.
   */
  setGnssMembership(payload: GnssMembershipPayload): void {
    if (this.destroyed) return;
    this.gnssMembership = buildMembershipIndex(payload);
    this.repaintGnss();
  }

  /**
   * Phase 9 chunk 2 — receive the latest overlay flag bundle from
   * the OverlayService subscription. Debounced at 50 ms per
   * docs/phase9.md § IV "GNSS recolor cost" mitigation so rapid-fire
   * toggle clicks don't repaint mid-frame.
   */
  setGnssOverlayFlags(flags: GnssOverlayFlags): void {
    if (this.destroyed) return;
    this.gnssFlags = { ...flags };
    if (this.gnssRepaintTimer !== null) clearTimeout(this.gnssRepaintTimer);
    this.gnssRepaintTimer = setTimeout(() => {
      this.gnssRepaintTimer = null;
      this.repaintGnss();
    }, 50);
  }

  /**
   * Re-apply default styling to every GNSS-member NORAD. Skips the
   * currently-highlighted satellite (selection color must survive).
   */
  private repaintGnss(): void {
    if (this.destroyed) return;
    for (const norad of this.gnssMembership.keys()) {
      if (norad === this.highlighted) continue;
      this.applyDefaultStyle(norad);
    }
  }

  /**
   * Phase 6 chunk 2 — multiply every dot's color alpha by `alpha` so
   * the catalog can be dimmed (or blanked) while the conjunction-replay
   * scene focuses on its two satellites.  Pass 1 to restore full
   * opacity (re-applies each NORAD's default style by type).
   *
   * Cheap — touches `count` PointPrimitives once.  Idempotent: passing
   * the same alpha repeatedly is fine.  Doesn't touch the highlighted
   * satellite (chunk-2 replay-mode handles that separately).
   */
  setOverallAlpha(alpha: number): void {
    const clamped = Math.max(0, Math.min(1, alpha));
    if (clamped === 1) {
      // Restore everyone to their type-default color.
      for (const [norad] of this.indexByNorad) {
        this.applyDefaultStyle(norad);
      }
      if (this.highlighted !== null) {
        // Re-apply the highlight on top so it survives the restore loop.
        const idx = this.indexByNorad.get(this.highlighted);
        if (idx !== undefined) {
          const p = this.collection.get(idx);
          p.color = HIGHLIGHT_COLOR;
          p.pixelSize = HIGHLIGHT_PIXEL_SIZE;
        }
      }
      return;
    }
    for (const [norad, idx] of this.indexByNorad) {
      const type = (this.typeByNorad.get(norad) ?? 'UNKNOWN') as keyof typeof COLORS;
      const base = COLORS[type] ?? COLORS.UNKNOWN;
      this.collection.get(idx).color = base.withAlpha(base.alpha * clamped);
    }
  }

  startPropagation(): void {
    if (this.unsubscribeTick !== null) return;
    // Kick once immediately so the user doesn't wait a full interval to see motion.
    this.requestPropagate(this.clock.getTimeMs());
    // Subscribe to Cesium's render-loop ticks (~60Hz max). Throttle below.
    this.unsubscribeTick = this.clock.onTick((timeMs) => this.maybePropagate(timeMs));
  }

  stopPropagation(): void {
    if (this.unsubscribeTick !== null) {
      this.unsubscribeTick();
      this.unsubscribeTick = null;
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.cancelHighlightAnimation();
    this.stopPropagation();
    this.unsubscribeMotion?.();
    this.unsubscribeMotion = null;
    if (this.gnssRepaintTimer !== null) {
      clearTimeout(this.gnssRepaintTimer);
      this.gnssRepaintTimer = null;
    }
    this.worker.terminate();
    if (!this.scene.isDestroyed()) {
      this.scene.primitives.remove(this.collection);
    }
  }

  /**
   * Called on every Cesium tick (~60Hz). Throttles to PROPAGATE_INTERVAL_MS,
   * EXCEPT when the clock made a big jump (e.g. user scrubbed the slider) —
   * then propagate immediately so the visible swarm catches up to the new time.
   */
  private maybePropagate(timeMs: number): void {
    if (this.destroyed) return;
    const realNow = performance.now();
    const elapsedReal = realNow - this.lastPropagateRealMs;
    const clockDelta = Math.abs(timeMs - this.lastPropagatedClockMs);
    if (elapsedReal < PROPAGATE_INTERVAL_MS && clockDelta < BIG_JUMP_MS) {
      return;
    }
    this.requestPropagate(timeMs);
  }

  private requestPropagate(timeMs: number): void {
    this.worker.postMessage({ type: 'propagate', timeMs });
    this.lastPropagatedClockMs = timeMs;
    this.lastPropagateRealMs = performance.now();
  }

  private onWorkerMessage(data: LoadedReply | PositionsReply): void {
    if (this.destroyed) return;
    if (data.type === 'loaded') {
      this.onStatusChange?.(`Tracking ${data.parsed.toLocaleString()} satellites`);
      return;
    }
    if (data.type === 'positions') {
      const { count, positions, noradIds } = data;
      for (let i = 0; i < count; i++) {
        const norad = noradIds[i];
        const idx = this.indexByNorad.get(norad);
        if (idx === undefined) continue;
        // worker emits km; Cesium wants meters.
        const cartesian = new Cesium.Cartesian3(
          positions[i * 3] * 1000,
          positions[i * 3 + 1] * 1000,
          positions[i * 3 + 2] * 1000,
        );
        this.collection.get(idx).position = cartesian;
        this.positionByNorad.set(norad, cartesian);
      }
    }
  }
}
